import React from 'react';
import { useTranslation } from 'react-i18next';
import useAppContext from '../../../../context/useAppContext';
import { ToastContext, DropdownLight, Modal, Button, StopPropagation } from '@weezevent/nacre';
import AttendeeModel from '../../../../models/attendeeModel';
import { useSegment } from '../../../../components/tracking/trackingProvider';

export const AttendeeModerationStatus = React.memo(({ attendee, detailedView = false, className = '', sync = () => {} }) => {
    const { t } = useTranslation();
    const { track } = useSegment();
    const [{ event, organization }] = useAppContext();

    const toast = React.useContext(ToastContext);
    const [displayModal, setDisplayModal] = React.useState(false);
    let [mStatus, setModerationStatus] = React.useState(attendee.moderation_status);
    let [isSubmitting, setIsSubmitting] = React.useState(false);
    const updateAttendee = React.useCallback(
        (notifyAttendee = false, status = null) => {
            setIsSubmitting(true);
            let _status = status || mStatus;
            AttendeeModel.perform_action_update_moderation_status({ moderation_status: _status, notify_attendee: notifyAttendee }, organization, event, attendee)
                .then(_ => {
                    toast.success(t('ticket.manager.common.moderation.toast.success'));
                })
                .catch(_ => {
                    toast.error(t('ticket.manager.common.moderation.toast.error'));
                })
                .finally(() => {
                    setIsSubmitting(false);
                    setDisplayModal(false);
                    sync();
                });
        },
        [attendee, toast, event, organization, sync, mStatus]
    );

    const changeStatus = React.useCallback(
        value => {
            setModerationStatus(value);
            if (value === 'pending') {
                updateAttendee(false, value);
            } else {
                setDisplayModal(true);
            }
        },
        [attendee, updateAttendee]
    );
    function getLabel(status) {
        return t('ticket.manager.common.moderation.' + status);
    }
    const moderationStatusItems = [
        { label: getLabel('pending'), value: 'pending' },
        { label: getLabel('validated'), value: 'validated' },
        { label: getLabel('rejected'), value: 'rejected' },
        { label: getLabel('uncomplete'), value: 'uncomplete' }
    ];
    let displayedModerationStatusItems = moderationStatusItems.filter(v => v.key !== mStatus);

    return (
        <>
            <StopPropagation>
                <DropdownLight
                    key="moderation_status"
                    className={className}
                    onChange={value => {
                        track('ACTION > CHANGE MODERATION', { 'Page Title': 'MANAGE ATTENDEES', 'Previous status': attendee.moderation_status, 'Next status': value });
                        changeStatus(value);
                    }}
                    value={mStatus}
                    items={displayedModerationStatusItems}
                    position="bottom-end"
                />
                {displayModal && (
                    <Modal
                        size="medium"
                        title={t('ticket.manager.common.moderation.informAttendee')}
                        open={displayModal}
                        submitButton={
                            <Button
                                primary
                                onClick={() => {
                                    updateAttendee(true);
                                }}
                                label={t('ticket.common.action.send')}
                                isLoading={isSubmitting}
                            />
                        }
                        cancelButton={
                            <Button
                                onClick={() => {
                                    updateAttendee(false);
                                }}
                                label={t('ticket.common.cta.doNotSend')}
                                isLoading={isSubmitting}
                            />
                        }
                        onClose={() => {
                            setDisplayModal(false);
                            setModerationStatus(attendee.moderation_status);
                        }}
                    >
                        {t('ticket.manager.common.moderation.informAttendeeText')}
                    </Modal>
                )}
            </StopPropagation>
        </>
    );
});
